import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Music, Plus, ChevronLeft, ChevronRight, CheckCircle, 
  Star, Clock, DollarSign, Users, Sparkles, ArrowRight,
  X, Search, Mic, Mic2, ListMusic, Drumstick, Volume2, ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/FirebaseAuthContext';
import { useCurrency } from '@/contexts/CurrencyContext';
import { db } from '@/lib/firebase';
import { doc, setDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { updateReferralSignupStage } from '@/lib/referralSystem';
import { debugLog } from '@/components/DebugConsole';
import { getMinimumPrice, formatMinimumPrice, getTestSongMinimumPrice, formatTestSongMinimumPrice } from '@/lib/currencyConfig';
import { persistAddSongWizardCompleted } from '@/lib/addSongWizardState';
import { ysr2Mobile } from '../styles/mobileDesignSystem';

const AddSongWizard = ({ isVisible, onComplete, onDismiss }) => {
  const { user, profile } = useAuth();
  const { toast } = useToast();
  const { currency, format } = useCurrency();
  const [currentStep, setCurrentStep] = useState(0);
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [expandedSongs, setExpandedSongs] = useState([0, 1, 2]); // First 3 songs expanded by default
  const quickAddRef = useRef(null);
  const modalContentRef = useRef(null);

  // Get minimum price for current currency
  const minimumPrice = getMinimumPrice(currency);
  const formattedMinimum = formatMinimumPrice(currency);
  const testSongMinimum = getTestSongMinimumPrice(currency);

  const getEffectivePriceMin = (form) => {
    const t = (form.title || '').trim().toLowerCase();
    const a = (form.artist || '').trim().toLowerCase();
    if (t === 'free' && a === 'free') return 0;
    if (t === 'test' && a === 'test') return testSongMinimum;
    return minimumPrice;
  };

  const formatMinLabelForForm = (form) => {
    const t = (form.title || '').trim().toLowerCase();
    const a = (form.artist || '').trim().toLowerCase();
    if (t === 'free' && a === 'free') return format(0);
    if (t === 'test' && a === 'test') return formatTestSongMinimumPrice(currency);
    return formattedMinimum;
  };

  // Song form state - simplified to only essential fields
  // Initialize with minimum price for current currency
  const [songForms, setSongForms] = useState([
    { title: '', artist: '', price: minimumPrice.toFixed(2) },
    { title: '', artist: '', price: minimumPrice.toFixed(2) },
    { title: '', artist: '', price: minimumPrice.toFixed(2) },
    { title: '', artist: '', price: minimumPrice.toFixed(2) },
    { title: '', artist: '', price: minimumPrice.toFixed(2) }
  ]);
  const [priceErrors, setPriceErrors] = useState({}); // Track price validation errors per song

  const totalSongs = 5;

  useEffect(() => {
    console.log('🔍 AddSongWizard: isVisible changed to:', isVisible);
    if (isVisible) {
      console.log('🎯 AddSongWizard: Setting showWelcome to true');
      setTimeout(() => {
        setShowWelcome(true);
      }, 500);
    }
  }, [isVisible]);

  const handleInputChange = (songIndex, field, value) => {
    setSongForms(prev => {
      const updated = [...prev];
      updated[songIndex] = { ...updated[songIndex], [field]: value };
      const currentForm = updated[songIndex];

      // Price validation when price, title, or artist changes (test/free flags depend on title+artist)
      if (field === 'price' || field === 'title' || field === 'artist') {
        const rawPrice = currentForm.price;
        const numValue = parseFloat(rawPrice);
        const isTestSong =
          currentForm.title.trim().toLowerCase() === 'test' &&
          currentForm.artist.trim().toLowerCase() === 'test';
        const isFreeSong =
          currentForm.title.trim().toLowerCase() === 'free' &&
          currentForm.artist.trim().toLowerCase() === 'free';

        setPriceErrors((prev) => {
          const newErrors = { ...prev };
          if (rawPrice === '' || isNaN(numValue)) {
            delete newErrors[songIndex];
          } else if (numValue < 0) {
            newErrors[songIndex] = `Price cannot be negative.`;
          } else if (isFreeSong && numValue >= 0) {
            delete newErrors[songIndex];
          } else if (!isTestSong && !isFreeSong && numValue < minimumPrice) {
            newErrors[songIndex] = `Minimum price is ${formattedMinimum}. We recommend ${format(minimumPrice)}-${format(minimumPrice + 1)} per request for better earnings.`;
          } else if (isTestSong && numValue < testSongMinimum) {
            newErrors[songIndex] = `Minimum price for test songs is ${format(testSongMinimum)}.`;
          } else {
            delete newErrors[songIndex];
          }
          return newErrors;
        });
      }

      return updated;
    });
  };

  const toggleSongExpansion = (songIndex) => {
    setExpandedSongs(prev => {
      if (prev.includes(songIndex)) {
        return prev.filter(index => index !== songIndex);
      } else {
        return [...prev, songIndex];
      }
    });
  };

  const scrollToQuickAdd = () => {
    if (quickAddRef.current && modalContentRef.current) {
      // Get the position of the quick add section relative to the modal content
      const quickAddElement = quickAddRef.current;
      const scrollContainer = modalContentRef.current;
      
      // Calculate the position relative to the scroll container
      const containerRect = scrollContainer.getBoundingClientRect();
      const elementRect = quickAddElement.getBoundingClientRect();
      const scrollTop = scrollContainer.scrollTop;
      const relativeTop = elementRect.top - containerRect.top + scrollTop;
      
      // Smooth scroll within the modal content
      scrollContainer.scrollTo({
        top: relativeTop - 20, // 20px offset from top
        behavior: 'smooth'
      });
    }
  };

  const getCompletedSongsCount = () => {
    return songForms.filter(form => form.title.trim() && form.artist.trim()).length;
  };

  const handleNextStep = async () => {
    const completedCount = getCompletedSongsCount();
    
    if (completedCount < totalSongs) {
      const remaining = totalSongs - completedCount;
      toast({
        title: "More Songs Needed",
        description: `You still need to add ${remaining} more song${remaining > 1 ? 's' : ''} to continue.`,
        variant: "destructive"
      });
      return;
    }

    // Validate all songs have valid prices before proceeding
    const invalidSongs = [];
    songForms.forEach((form, index) => {
      if (form.title.trim() && form.artist.trim()) {
        const price = parseFloat(form.price) || 0;
        const isTestSong = form.title.trim().toLowerCase() === 'test' && 
                          form.artist.trim().toLowerCase() === 'test';
        
        if (!isTestSong && (isNaN(price) || price < minimumPrice)) {
          invalidSongs.push({
            index: index + 1,
            title: form.title.trim(),
            artist: form.artist.trim(),
            price: price,
            minimum: minimumPrice
          });
          // Set error state for this song
          setPriceErrors(prev => ({
            ...prev,
            [index]: `Minimum price is ${formattedMinimum}. We recommend ${format(minimumPrice)}-${format(minimumPrice + 1)} per request for better earnings.`
          }));
        } else if (isTestSong && (isNaN(price) || price < testSongMinimum)) {
          invalidSongs.push({
            index: index + 1,
            title: form.title.trim(),
            artist: form.artist.trim(),
            price: price,
            minimum: testSongMinimum,
            isTest: true
          });
          setPriceErrors(prev => ({
            ...prev,
            [index]: `Minimum price for test songs is ${format(testSongMinimum)}.`
          }));
        }
      }
    });

    if (invalidSongs.length > 0) {
      // Create a detailed error message
      const songDetails = invalidSongs.map(s => 
        `Song ${s.index}: "${s.title}" by ${s.artist} - Currently ${format(s.price)}, needs to be at least ${format(s.minimum)}`
      ).join('\n\n');

      toast({
        title: "⚠️ Price Too Low - Cannot Save",
        description: `Some songs are priced below the minimum requirement of ${formattedMinimum} per song.\n\n${songDetails}\n\nPlease update all song prices to at least ${formattedMinimum} to continue. We recommend starting at ${format(minimumPrice)}-${format(minimumPrice + 1)} per request for better earnings.`,
        variant: "destructive",
        duration: 10000 // Show for 10 seconds so user can read it
      });
      return;
    }

    // All songs are complete and valid, proceed to save
    await completeWizard();
  };

  const completeWizard = async () => {
    setLoading(true);
    
    try {
      // Filter out empty songs and prepare for saving
      const songsToSave = songForms
        .filter(form => form.title.trim() && form.artist.trim())
        .map((form, index) => {
          const price = parseFloat(form.price) || minimumPrice;
          
          // Test song: title+artist "test" — min price from getTestSongMinimumPrice (USD $1, else e.g. £0.50)
          const isTestSong = form.title.trim().toLowerCase() === 'test' && 
                            form.artist.trim().toLowerCase() === 'test';
          // Workaround: Allow £0.00 if both title and artist are "free" (case-insensitive)
          const isFreeSong = form.title.trim().toLowerCase() === 'free' && 
                            form.artist.trim().toLowerCase() === 'free';
          
          // Validate minimum price: currency-aware minimum (test songs: USD $1 or regional test min, £0.00 for free songs)
          if (price < 0) {
            throw new Error(`Price cannot be negative`);
          }
          
          if (isFreeSong && price >= 0) {
            // Free song - allow £0.00, no validation needed
          } else if (!isTestSong && !isFreeSong && price < minimumPrice) {
            throw new Error(`Song "${form.title}" by "${form.artist}" has a price below the minimum of ${formattedMinimum}`);
          } else if (isTestSong && price < testSongMinimum) {
            throw new Error(`Test song has a price below the minimum of ${format(testSongMinimum)}`);
          }
          
          return {
            title: form.title.trim(),
            artist: form.artist.trim(),
            price: price,
            id: Date.now() + index,
            addedAt: new Date(),
            status: 'active'
          };
        });

      debugLog('ADD_SONGS', `Starting to add ${songsToSave.length} songs`, { 
        userId: user.uid, 
        songCount: songsToSave.length,
        songs: songsToSave.map(s => ({ title: s.title, artist: s.artist, price: s.price }))
      });

      // Save songs to Firestore - use main songs collection
      for (const song of songsToSave) {
        await addDoc(collection(db, 'songs'), {
          title: song.title,
          original_artist: song.artist,
          price: song.price,
          user_id: user.uid,
          created_at: serverTimestamp(),
          archived: false,
          order: songsToSave.indexOf(song) + 1
        });
      }

      debugLog('SUCCESS', `Successfully added ${songsToSave.length} songs to Firestore`, { 
        userId: user.uid, 
        songCount: songsToSave.length 
      });

      // Update user profile to mark onboarding as complete
      const userRef = doc(db, 'users', user.uid);
      await setDoc(userRef, {
        onboardingComplete: true,
        songsAdded: songsToSave.length,
        lastUpdated: serverTimestamp()
      }, { merge: true });

      toast({
        title: "🎉 Welcome to Your Song Request!",
        description: `Successfully added ${songsToSave.length} songs to your catalogue. You're ready to start receiving requests!`,
      });

      await persistAddSongWizardCompleted(db, user.uid);

      // Update referral signup stage if this is a referred user
      await updateReferralSignupStage(user.uid);

      onComplete();
    } catch (error) {
      console.error('Error saving songs:', error);
      toast({
        title: "Error",
        description: "There was an error saving your songs. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center space-y-6"
          >
            <div className="space-y-4">
              <div className="w-20 h-20 mx-auto bg-gradient-to-br from-purple-600 to-blue-600 rounded-full flex items-center justify-center">
                <Music className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white">Let's Build Your Song Catalogue!</h2>
              <p className="text-gray-300 max-w-md mx-auto">
                Add at least 5 songs to your catalogue to get started. This will give your audience 
                a great selection to choose from when making requests.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto">
              <Card className="bg-gray-900/50 border-gray-700">
                <CardContent className="p-4 text-center">
                  <button
                    onClick={scrollToQuickAdd}
                    className="w-full h-full flex flex-col items-center justify-center hover:opacity-80 transition-opacity cursor-pointer"
                  >
                    <div className="w-12 h-12 mx-auto bg-blue-600/20 rounded-full flex items-center justify-center mb-3 hover:bg-blue-600/30 transition-colors">
                      <Plus className="w-6 h-6 text-blue-400" />
                    </div>
                    <h3 className="font-semibold text-white mb-2">Add Songs</h3>
                    <p className="text-sm text-gray-400">Enter track title, artist & price</p>
                  </button>
                </CardContent>
              </Card>

              <Card className="bg-gray-900/50 border-gray-700">
                <CardContent className="p-4 text-center">
                  <div className="w-12 h-12 mx-auto bg-green-600/20 rounded-full flex items-center justify-center mb-3">
                    <CheckCircle className="w-6 h-6 text-green-400" />
                  </div>
                  <h3 className="font-semibold text-white mb-2">Set Prices</h3>
                  <p className="text-sm text-gray-400">Choose request prices</p>
                </CardContent>
              </Card>

              <Card className="bg-gray-900/50 border-gray-700">
                <CardContent className="p-4 text-center">
                  <div className="w-12 h-12 mx-auto bg-purple-600/20 rounded-full flex items-center justify-center mb-3">
                    <Star className="w-6 h-6 text-purple-400" />
                  </div>
                  <h3 className="font-semibold text-white mb-2">Go Live</h3>
                  <p className="text-sm text-gray-400">Start receiving requests</p>
                </CardContent>
              </Card>
            </div>

            {/* Quick Add Song Section */}
            <div className="max-w-md mx-auto" ref={quickAddRef}>
              <Card className="bg-gray-900/50 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white text-center">Quick Add Your First Song</CardTitle>
                  <CardDescription className="text-gray-400 text-center">
                    Start building your catalogue right now
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="quick-title" className="text-white">Track Title *</Label>
                      <Input
                        id="quick-title"
                        value={songForms[0].title}
                        onChange={(e) => handleInputChange(0, 'title', e.target.value)}
                        placeholder="e.g., Bohemian Rhapsody"
                        className="bg-gray-800 border-gray-600 text-white"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="quick-artist" className="text-white">Artist *</Label>
                      <Input
                        id="quick-artist"
                        value={songForms[0].artist}
                        onChange={(e) => handleInputChange(0, 'artist', e.target.value)}
                        placeholder="e.g., Queen"
                        className="bg-gray-800 border-gray-600 text-white"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="quick-price" className="text-white">
                      Request Price ({format(0)}) *
                      <span className="text-xs text-gray-400 ml-2">Minimum: {formatMinLabelForForm(songForms[0])}</span>
                    </Label>
                    <Input
                      id="quick-price"
                      type="number"
                      min={getEffectivePriceMin(songForms[0])}
                      step="0.01"
                      value={songForms[0].price}
                      onChange={(e) => handleInputChange(0, 'price', e.target.value)}
                      className={`bg-gray-800 border-gray-600 text-white ${
                        priceErrors[0] ? 'border-red-500 focus:border-red-500' : ''
                      }`}
                    />
                    {priceErrors[0] && (
                      <p className="text-sm text-red-400 mt-1 flex items-start">
                        <span className="mr-1">⚠️</span>
                        <span>{priceErrors[0]}</span>
                      </p>
                    )}
                    {!priceErrors[0] && songForms[0].price && parseFloat(songForms[0].price) >= getEffectivePriceMin(songForms[0]) && (
                      <p className="text-xs text-green-400 mt-1">✓ Price meets minimum requirement</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Progress and Action Buttons */}
            <div className="space-y-4">
              {/* Progress Indicator */}
              <div className="flex items-center justify-center space-x-2">
                {Array.from({ length: totalSongs }, (_, i) => (
                  <div
                    key={i}
                    className={`w-3 h-3 rounded-full ${
                      songForms[i]?.title?.trim() && songForms[i]?.artist?.trim() 
                        ? 'bg-green-500' 
                        : 'bg-gray-600'
                    }`}
                  />
                ))}
              </div>
              <p className="text-gray-400 text-sm">
                {getCompletedSongsCount()} of {totalSongs} songs completed
              </p>

              {/* Action Buttons */}
              <div className="flex justify-center space-x-4">
                {getCompletedSongsCount() >= totalSongs ? (
                  <>
                    <Button
                      onClick={() => {
                        // Add another song by expanding the next available slot
                        const nextEmptyIndex = songForms.findIndex(form => !form.title.trim() || !form.artist.trim());
                        if (nextEmptyIndex !== -1) {
                          setExpandedSongs(prev => [...prev, nextEmptyIndex]);
                        }
                      }}
                      variant="outline"
                      className="border-gray-600 text-gray-300 hover:bg-gray-800"
                    >
                      Add Another Song
                      <Plus className="w-4 h-4 ml-2" />
                    </Button>
                    
                    <Button
                      onClick={handleNextStep}
                      disabled={loading}
                      className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-8"
                    >
                      {loading ? (
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Saving...
                        </div>
                      ) : (
                        <>
                          Complete Setup Wizard
                          <CheckCircle className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={() => setCurrentStep(1)}
                    className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-8 py-3"
                  >
                    Continue Adding Songs
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        );

      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="max-w-2xl mx-auto space-y-6"
          >
            {/* Progress Header */}
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center space-x-2">
                {Array.from({ length: totalSongs }, (_, i) => (
                  <div
                    key={i}
                    className={`w-3 h-3 rounded-full ${
                      songForms[i]?.title?.trim() && songForms[i]?.artist?.trim() 
                        ? 'bg-green-500' 
                        : 'bg-gray-600'
                    }`}
                  />
                ))}
              </div>
              <h2 className="text-xl font-semibold text-white">
                Add at least 5 songs to your catalogue
              </h2>
              <p className="text-gray-400">
                {getCompletedSongsCount()} of {totalSongs} songs completed
              </p>
            </div>

            {/* Song Forms */}
            <div className="space-y-4">
              {songForms.map((form, index) => (
                <Card 
                  key={index} 
                  className={`bg-gray-900/50 border-gray-700 ${
                    expandedSongs.includes(index) ? 'border-purple-500/50' : ''
                  }`}
                >
                  <CardHeader 
                    className="pb-3 cursor-pointer"
                    onClick={() => toggleSongExpansion(index)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          form.title.trim() && form.artist.trim() 
                            ? 'bg-green-600/20 border border-green-500/50' 
                            : 'bg-gray-700/50 border border-gray-600'
                        }`}>
                          {form.title.trim() && form.artist.trim() ? (
                            <CheckCircle className="w-4 h-4 text-green-400" />
                          ) : (
                            <span className="text-sm text-gray-400">{index + 1}</span>
                          )}
                        </div>
                        <div>
                          <CardTitle className="text-white text-lg">
                            Song {index + 1}
                          </CardTitle>
                          <CardDescription className="text-gray-400">
                            {form.title.trim() && form.artist.trim() 
                              ? `${form.title} by ${form.artist}` 
                              : 'Click to add song details'
                            }
                          </CardDescription>
                        </div>
                      </div>
                      {expandedSongs.includes(index) ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </CardHeader>
                  
                  <AnimatePresence>
                    {expandedSongs.includes(index) && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <CardContent className="pt-0 space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor={`title-${index}`} className="text-white">Track Title *</Label>
                              <Input
                                id={`title-${index}`}
                                value={form.title}
                                onChange={(e) => handleInputChange(index, 'title', e.target.value)}
                                placeholder="e.g., Bohemian Rhapsody"
                                className="bg-gray-800 border-gray-600 text-white"
                                required
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor={`artist-${index}`} className="text-white">Artist *</Label>
                              <Input
                                id={`artist-${index}`}
                                value={form.artist}
                                onChange={(e) => handleInputChange(index, 'artist', e.target.value)}
                                placeholder="e.g., Queen"
                                className="bg-gray-800 border-gray-600 text-white"
                                required
                              />
                            </div>
                          </div>
                          
                          <div className="space-y-2">
                            <Label htmlFor={`price-${index}`} className="text-white">
                              Request Price ({format(0)}) *
                              <span className="text-xs text-gray-400 ml-2">Minimum: {formatMinLabelForForm(form)}</span>
                            </Label>
                            <Input
                              id={`price-${index}`}
                              type="number"
                              min={getEffectivePriceMin(form)}
                              step="0.01"
                              value={form.price}
                              onChange={(e) => handleInputChange(index, 'price', e.target.value)}
                              className={`bg-gray-800 border-gray-600 text-white ${
                                priceErrors[index] ? 'border-red-500 focus:border-red-500' : ''
                              }`}
                            />
                            {priceErrors[index] && (
                              <p className="text-sm text-red-400 mt-1 flex items-start">
                                <span className="mr-1">⚠️</span>
                                <span>{priceErrors[index]}</span>
                              </p>
                            )}
                            {!priceErrors[index] && form.price && parseFloat(form.price) >= getEffectivePriceMin(form) && (
                              <p className="text-xs text-green-400 mt-1">✓ Price meets minimum requirement</p>
                            )}
                          </div>
                        </CardContent>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Card>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-center space-x-4">
              {getCompletedSongsCount() >= totalSongs ? (
                <>
                  <Button
                    onClick={() => {
                      // Add another song by expanding the next available slot
                      const nextEmptyIndex = songForms.findIndex(form => !form.title.trim() || !form.artist.trim());
                      if (nextEmptyIndex !== -1) {
                        setExpandedSongs(prev => [...prev, nextEmptyIndex]);
                      }
                    }}
                    variant="outline"
                    className="border-gray-600 text-gray-300 hover:bg-gray-800"
                  >
                    Add Another Song
                    <Plus className="w-4 h-4 ml-2" />
                  </Button>
                  
                  <Button
                    onClick={handleNextStep}
                    disabled={loading}
                    className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-8"
                  >
                    {loading ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </div>
                    ) : (
                      <>
                        Complete Setup Wizard
                        <CheckCircle className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </>
              ) : (
                <Button
                  onClick={handleNextStep}
                  disabled={loading}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-8"
                >
                  {loading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </div>
                  ) : (
                    <>
                      Add Another Song
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              )}
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  if (!isVisible) {
    return null;
  }
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100000] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className={`${ysr2Mobile.surfaceStrong} max-w-4xl w-full max-h-[90dvh] overflow-y-auto overscroll-contain`}
            ref={modalContentRef}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-blue-300/10">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-blue-600 rounded-full flex items-center justify-center">
                  <Music className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">Add Song Wizard</h1>
                  <p className="text-sm text-gray-400">Build your song catalogue</p>
                </div>
              </div>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => (onDismiss ? onDismiss() : onComplete?.())}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Content */}
            <div className="p-6">
              {getStepContent()}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AddSongWizard;
